import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { WorkflowSectionsActions } from '../actions/workflow-sections.actions';
import { WorkflowsActions } from '../actions/workflows.actions';
import { WorkflowDto } from '../dtos/workflow-dto.interface';
import { WorkflowSectionDto } from '../dtos/workflow-section-dto.interface';
import { SubjectType } from '../enums/subject-type.enum';
import { WorkflowArea } from '../enums/workflow-area.enum';

export interface WorkflowsState extends EntityState<WorkflowDto> {
    sections: WorkflowSectionDto[];
    selectedWorkflowId: string | null;
    selectedWorkflowAsTemplateId: string | null; // Optional for template selection
    loading: boolean;
    error: string | null;
}

// Entity adapter
export const workflowsMetadataAdapter = createEntityAdapter<WorkflowDto>({
    selectId: (workflow) => workflow.workflowId,
});

// Initial state
export const initialWorkflowOverviewState: WorkflowsState = workflowsMetadataAdapter.getInitialState({
    sections: [],
    selectedWorkflowId: null,
    selectedWorkflowAsTemplateId: null,
    loading: false,
    error: null,
});

export const workflowsReducer = createReducer(
    initialWorkflowOverviewState,

    // Load Workflows
    on(WorkflowsActions.personMandateManagementWorkflowLoaded, (state, { response }) =>
        workflowsMetadataAdapter.upsertMany(response, state),
    ),
    on(WorkflowsActions.loadWorkflows, (state) => ({ ...state, loading: true })),
    on(WorkflowsActions.workflowsLoaded, (state, { response }) => {
        return workflowsMetadataAdapter.setAll(response, {
            ...state,
            loading: false,
            error: null,
        });
    }),
    on(WorkflowsActions.workflowsLoadFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Load Workflow By Id
    on(WorkflowsActions.loadWorkflowById, (state) => ({ ...state, loading: true })),
    on(WorkflowsActions.workflowByIdLoaded, (state, { response }) => ({
        ...state,
        selectedWorkflowId: response.workflowId,
        loading: false,
        error: null,
    })),
    on(WorkflowsActions.workflowByIdLoadFailure, (state, { error }) => ({
        ...state,
        selectedWorkflowId: null,
        loading: false,
        error: error.message,
    })),

    // Create Workflow
    on(WorkflowsActions.createWorkflow, (state) => ({ ...state, loading: true })),
    on(WorkflowsActions.workflowCreated, (state, { response }) =>
        workflowsMetadataAdapter.addOne(response, {
            ...state,
            loading: false,
            selectedWorkflowId: response.workflowId,
        }),
    ),
    on(WorkflowsActions.workflowCreateFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Update Workflow
    on(WorkflowsActions.updateWorkflow, (state) => ({ ...state, loading: true })),
    on(WorkflowsActions.workflowUpdated, (state, { response }) =>
        workflowsMetadataAdapter.updateOne(
            { id: response.workflowId, changes: response },
            { ...state, loading: false },
        ),
    ),
    on(WorkflowsActions.workflowUpdateFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Delete Workflow
    on(WorkflowsActions.deleteWorkflow, (state) => ({ ...state, loading: true })),
    on(WorkflowsActions.workflowDeleted, (state, action) =>
        workflowsMetadataAdapter.removeOne(action.workflowId, { ...state, loading: false }),
    ),
    on(WorkflowsActions.workflowUpdateFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Reflect update of section in workflow
    on(WorkflowSectionsActions.sectionUpdated, (state, { response }) => {
        const workflowToUpdate = Object.values(state.entities).find((wf) =>
            wf?.sections.some((sec) => sec.id === response.id),
        );

        if (!workflowToUpdate) return state;

        const updatedSections = workflowToUpdate.sections.map((sec) => (sec.id === response.id ? response : sec));

        return workflowsMetadataAdapter.updateOne(
            {
                id: workflowToUpdate.workflowId,
                changes: {
                    sections: updatedSections,
                },
            },
            state,
        );
    }),

    on(WorkflowsActions.setSelectedWorkflowId, (state, { workflowId }) => ({
        ...state,
        selectedWorkflowId: workflowId,
        loading: false,
        error: null,
    })),

    on(WorkflowsActions.setSelectedWorkflowAsTemplateId, (state, { workflowId }) => ({
        ...state,
        selectedWorkflowAsTemplateId: workflowId,
        loading: false,
        error: null,
    })),
);

export const workflowsFeature = createFeature({
    name: 'aumicoWorkflowsFeature',
    reducer: workflowsReducer,
});

export const {
    selectAumicoWorkflowsFeatureState,
    selectSections,
    selectSelectedWorkflowId,
    selectSelectedWorkflowAsTemplateId,
    selectLoading,
    selectError,
} = workflowsFeature;

const { selectAll, selectEntities } = workflowsMetadataAdapter.getSelectors();

export const selectAllWorkflows = createSelector(selectAumicoWorkflowsFeatureState, selectAll);

export const selectWorkflowEntities = createSelector(selectAumicoWorkflowsFeatureState, selectEntities);

export const selectSelectedWorkflow = createSelector(
    selectWorkflowEntities,
    selectSelectedWorkflowId,
    (entities, selectedId) => (selectedId ? entities[selectedId] : null),
);

export const selectSelectedWorkflowAsTemplate = createSelector(
    selectWorkflowEntities,
    selectSelectedWorkflowAsTemplateId,
    (entities, selectedId) => (selectedId ? entities[selectedId] : null),
);

/** REV-22155 — mandate management template of a type; COMPANY = templates without type or COMPANY. */
export const selectMandateManagementWorkflowOf = (subjectType: SubjectType) =>
    createSelector(selectAllWorkflows, (workflows) =>
        workflows.find(
            (wf) =>
                wf.area === WorkflowArea.MANDATE_MANAGEMENT &&
                (wf.subjectType ?? SubjectType.COMPANY) === subjectType,
        ),
    );

/** Company template — as before persons, existing callers stay on the company template. */
export const selectMandateManagementWorkflow = selectMandateManagementWorkflowOf(SubjectType.COMPANY);

export const selectAllClosingWorkflows = createSelector(selectAllWorkflows, (workflows) =>
    workflows.filter((wf) => wf.area === WorkflowArea.CLOSING_PROCESS),
);
